import React, { useState } from "react";
import Navbar from "../../components/Navbar/Navbar";
import { useNavigate } from "react-router-dom";
import {
  Table,
  Select,
  Button,
  Modal,
  Row,
  Col,
  Form,
  DatePicker,
  Input,
  Tabs,
  Space, Upload
} from "antd";
import {
  DeleteOutlined,
  EditOutlined,
  ExclamationCircleOutlined,
  PlusCircleOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  EyeOutlined, UploadOutlined
} from "@ant-design/icons";
import dayjs from "dayjs";
import moment from "moment";
import "quill/dist/quill.snow.css";
import { UseGetRequest } from "../../utils/api/ApiRequests";
import { GetAllEmployees } from "../../utils/api/DropdownFiltersApi";
import { usePostRequest } from "../../hooks/usePostRequest";
import ReactQuillEditor from "../../components/ReactQuillEditor/ReactQuillEditor";
import {
  capitalizeEachWord,
  getEmployeeId,
  getIsAdminLogin,
} from "../../utils/Common";
import * as XLSX from "xlsx";
import toast from "react-hot-toast";

const AddSubmitButton = ({ form, isLoading }) => {
  const [submittable, setSubmittable] = useState(false);
  const values = Form.useWatch([], form);
  React.useEffect(() => {
    form
      .validateFields({
        validateOnly: true,
      })
      .then(
        () => {
          setSubmittable(true);
        },
        () => {
          setSubmittable(false);
        }
      );
  }, [values]);

  return (
    <Button
      type="primary"
      htmlType="submit"
      disabled={!submittable}
      loading={isLoading}
    >
      Submit
    </Button>
  );
};

const { Option } = Select;

export default function CommunicationLetter() {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const isAdminLogin = getIsAdminLogin();
  const employeeId = getEmployeeId();
  const [content, setContent] = useState("");
  const [tableData, setTableData] = useState([]);
  const [isFetching, setIsFetching] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [clickedRowData, setClickedRowData] = useState({});
  const [letterTypes, setLetterTypes] = useState([]);
  const [fileAttachments, setFileAttachments] = useState([]);
  const [modal, contextHolder] = Modal.useModal();
  const employeeOptions = GetAllEmployees();
  const dateFormat = "YYYY-MM-DD";
  const [searchTerm, setSearchTerm] = useState("");

  const dropdownOptions = {
    employee: employeeOptions.slice(1),
  };

  const { callPost, isLoading } = usePostRequest(() => {
    setShowAddModal(false);
    setShowEditModal(false);
    setShowViewModal(false);
    setShowUploadModal(false);
    resetValues();
    fetchTableData();
  });

  const [filterConfig, setFilterConfig] = useState({
    employee_id: isAdminLogin ? null : employeeId,
    status: isAdminLogin ? "pending" : "approved",
  });

  const tableColumns = [
    {
      title: "Code",
      dataIndex: "code",
      key: "code",
    },
    {
      title: "Date Posted",
      dataIndex: "letter_date",
      key: "letter_date",
      render: (letter_date, record) => (
        <div>{moment(letter_date).format("MMM DD, YYYY")}</div>
      ),
    },
    {
      title: "Subject",
      dataIndex: "subject",
      key: "subject",
    },
    {
      title: "Letter From",
      dataIndex: "letter_from",
      key: "letter_from",
    },
    {
      title: "Letter To",
      dataIndex: "details",
      key: "details",
      render: (details, record) => (
        <ul
          onClick={() => handleRowClick(record)}
          className="pointer-cursor custom-list"
        >
          {details.map((value, index) => (
            <li key={index}>{value.letter_to}</li>
          ))}
        </ul>
      ),
    },
    {
      title: "Prepared By",
      dataIndex: "added_by_name",
      key: "added_by_name",
      render: (data, record) => (
        <div>
          <Row>{data}</Row>
          <Row>{moment(record.added_on).format("MMM DD, YYYY hh:mm A")}</Row>
        </div>
      ),
    },
    {
      title: "Approved By",
      dataIndex: "approved_by_name",
      key: "approved_by_name",
      render: (data, record) => (
        <div>
          <Row>{data}</Row>
          <Row>
            {record.status === "approved" &&
              moment(record.updated_on).format("MMM DD, YYYY hh:mm A")}
          </Row>
        </div>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (data) => <div>{capitalizeEachWord(data)}</div>,
    },
  ];

  const onFinish = (formInputs) => {
    formInputs.letter_date = formInputs.letter_date.format(dateFormat);
    formInputs.letter_details = formInputs.recipients;
    formInputs.status = "pending";
    callPost("letters/add", formInputs, false);
  };

  const onEdit = (formInputs) => {
    formInputs.letter_id = clickedRowData.id;
    formInputs.letter_date = formInputs.letter_date.format(dateFormat);
    formInputs.letter_details = formInputs.recipients;
    callPost(`letters/update`, formInputs, false);
  };

  const confirmDelete = (letter) => {
    modal.confirm({
      title: `Are you sure you want to delete this letter`,
      icon: <ExclamationCircleOutlined />,
      content: "You cannot undo this action.",
      okText: "Delete",
      cancelText: "Cancel",
      onOk: () => handleDelete(letter.id),
      okType: "danger",
    });
  };

  function handleDelete(passedId) {
    callPost("letters/delete", { letter_id: passedId }, false);
  }

  const confirmApprove = (leave, status) => {
    modal.confirm({
      title: `Are you sure you want to ${status} this letter?`,
      icon: <CheckCircleOutlined />,
      //   content: "You cannot undo this action.",
      okText: "Confirm",
      cancelText: "Cancel",
      onOk: () => handleApprove(leave.id, status),
      //   okType: "danger",
    });
  };

  function handleApprove(passedId, status) {
    const newStatus = `${status}d`;
    callPost(
      "letters/approve",
      { letter_id: passedId, status: newStatus },
      false
    );
  } 

  async function handleUploadRequirements () {
   const payload = {
      letter_id: clickedRowData.id,
      letter_file_id: [],
      letter_file: [],
    }

    const emptyFile = new File([""], "", { type: "application/octet-stream" });

    (fileAttachments ?? []).forEach((file) => {
      if (file.attachment_id) {
        payload.letter_file.push(emptyFile);
        payload.letter_file_id.push(file.attachment_id);
      } else {
        payload.letter_file.push(file);
        payload.letter_file_id.push("");
      }
    });
    const formValues = Object.entries(payload).map(([key, value]) => ({key, value}))
    callPost("letters/upload_attachments", formValues, true);
  }

  async function urlToFile(fileUrl, fileName) {
    try {
      const response = await fetch(fileUrl);
      const blob = await response.blob();
      const file = new File([blob], fileName, { type: blob.type });
      return file;
    } catch (error) {
      return null;
    }
  }

  async function fetchInitialAttachments () {
    if (clickedRowData.attachments && clickedRowData.attachments.length>0) {
      const tempFilesPromises = clickedRowData.attachments.map(async (file) => {
        const tempFile = await urlToFile(file.file_url, file.file_name);
        return {
          attachment_id: file.id,
          file: tempFile,
          uid: file.id,
          name: file.file_name,
          status: "done",
          url: file.file_url,
        };
      });
  
      const tempFiles = await Promise.all(tempFilesPromises);
      setFileAttachments(tempFiles)
    } else {
      setFileAttachments([])
    }

    setShowViewModal(false);
    setShowUploadModal(true)
  }

  function resetValues() {
    form.resetFields();
    setContent("");
    setFileAttachments([]);
  }

  const handleSearch = (e) => {
    if (e.key === "Enter") {
      setFilterConfig((prev) => ({ ...prev, term: searchTerm }));
    }
  };

  const handleRowClick = (rowData) => {
    if (isAdminLogin) {
      setContent(rowData.content);
      setClickedRowData(rowData);
      const formattedValues = { ...rowData };
      formattedValues.letter_date = rowData.letter_date
        ? dayjs(rowData.letter_date, dateFormat)
        : "";
      form.setFieldsValue(formattedValues);
      setShowViewModal(true);
    } else {
      navigate("view", { state: rowData });
    }
  };

  const rowProps = (record) => ({
    onClick: () => handleRowClick(record),
  });

  const handleQuillChange = (html) => {
    setContent(html);
    form.setFieldValue("content", html);
  };

  function exportToExcel() {
    const excelData = [tableColumns.map((col) => col.title)]; // insert header titles first
    // populate excel data
    tableData.forEach((data) => {
      let tempArray = tableColumns.map((col) => {
        if (Array.isArray(data[col.key])) {
          data[col.key].forEach((row) => {
            return row.letter_to;
          });
        } else {
          return data[col.key];
        }
      });
      excelData.push(tempArray);
    });
    const workbook = XLSX.utils.book_new();
    const worksheet = XLSX.utils.aoa_to_sheet(excelData);
    XLSX.utils.book_append_sheet(workbook, worksheet, "Sheet1");
    const excelBlob = new Blob(
      [XLSX.write(workbook, { bookType: "xlsx", type: "array" })],
      { type: "application/octet-stream" }
    );
    const excelUrl = URL.createObjectURL(excelBlob);
    const downloadAnchor = document.createElement("a");
    downloadAnchor.href = excelUrl;
    downloadAnchor.download = "Communication Letters.xlsx";
    downloadAnchor.click();
    setTimeout(() => {
      URL.revokeObjectURL(excelUrl);
    }, 100);
  }

  async function fetchTableData() {
    setIsFetching(true);
    setTableData([]);
    const response = await UseGetRequest("letters/get", filterConfig);
    if (response.data) {
      const res = response.data.data?.letters?.map((data) => {
        const info = data;
        info.recipients = data.details.map((rec) => {
          return {
            type: rec.type,
            letter_to: rec.letter_to_id,
          }
        });
        return info;
      });
      setTableData(res);
    }
    setIsFetching(false);
  }

  React.useEffect(() => {
    async function fetchLetterTypes() {
      const response = await UseGetRequest("letter_types/get", {});
      if (response.data) {
        const res = response.data.data.map((data) => {
          let info = {};
          info.value = data.id;
          info.label = data.name;
          return info;
        });
        setLetterTypes(res);
      }
    }
    fetchLetterTypes();
  }, []);

  React.useEffect(() => {
    fetchTableData();
  }, [filterConfig]);

  return (
    <Navbar
      activeNav="communication"
      pageTitle="Communication Letters"
      pageButtonTitle="Add Communication Letter"
      onClickButton={() => {
        setShowAddModal(true);
        setShowViewModal(false);
      }}
      setSearchQuery={(e) => setSearchTerm(e)}
      onSearch={handleSearch}
      searchPlaceholder="subject"
      isManager
      searchable
      noButton={!isAdminLogin}
      pageDownloadButtonTitle="Download Excel"
      isDownloadBtn
      onClickDownloadButton={exportToExcel}
    >
      <Tabs
        defaultActiveKey="pending"
        className="mt-4"
        items={[
          { key: "pending", label: "Pending" },
          { key: "approved", label: "Approved" },
          { key: "disapproved", label: "Disapproved" },
          { key: "", label: "All" },
        ]}
        type="card"
        onChange={(e) => setFilterConfig((prev) => ({ ...prev, status: e }))}
      />
      <Space className="justify-content-start" wrap>
        <Select
          showSearch
          style={{ width: 200 }}
          placeholder="Employee"
          optionFilterProp="items"
          filterOption={(input, option) =>
            (option?.label ?? "").includes(input)
          }
          filterSort={(optionA, optionB) => {
            if (optionA.label?.includes("All")) return -1;
            if (optionB.label?.includes("All")) return 1;
            return optionA.label
              .toLowerCase()
              .localeCompare(optionB.label.toLowerCase());
          }}
          options={employeeOptions}
          onChange={(e) =>
            setFilterConfig((prev) => ({ ...prev, employee_id: e }))
          }
        />
      </Space>
      <Table
        className=""
        rowKey="id"
        columns={tableColumns}
        dataSource={tableData}
        loading={isFetching}
        onRow={(record) => ({
          ...rowProps(record),
          style: { cursor: "pointer" },
        })}
      />

      <Modal
        maskClosable={false}
        title={`${showAddModal ? "Add" : "Edit"} Communication Letter`}
        centered
        open={showAddModal || showEditModal}
        footer={null}
        onCancel={() => {
          setShowAddModal(false);
          setShowEditModal(false);
          resetValues();
        }}
        width={800}
      >
        <Form
          className="mt-3 mx-3"
          form={form}
          name="validateOnly"
          layout="vertical"
          onFinish={showAddModal ? onFinish : onEdit}
        >
          <Row>
            <Col xs={24} lg={8}>
              <Form.Item
                name="letter_date"
                label="Date Posted"
                rules={[
                  {
                    required: true
                  },
                ]}
              >
                <DatePicker placeholder="YYYY-MM-DD" />
              </Form.Item>
            </Col>
            <Col xs={24} lg={8}>
              <Form.Item
                name="letter_from"
                label="Letter From"
                rules={[
                  {
                    required: true, whitespace: true,
                  },
                ]}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col xs={24} lg={8}>
              <Form.Item
                name="letter_type_id"
                label="Letter Type"
                rules={[
                  {
                    required: true,
                  },
                ]}
              >
                <Select options={letterTypes} />
              </Form.Item>
            </Col>
            <Col xs={24} lg={24}>
              <Form.Item
                name="subject"
                label="Subject"
                rules={[
                  {
                    required: true, whitespace: true,
                  },
                ]}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col xs={24} lg={24}>
              <Form.Item
                name="content"
                label="Content"
                rules={[
                  {
                    required: true,
                  },
                ]}
              >
                <ReactQuillEditor
                  content={content}
                  handleQuillChange={handleQuillChange}
                />
              </Form.Item>
            </Col>
          </Row>
          <Row>
            <span className="red me-1">*</span> Recipients
            <Form.List name="recipients" initialValue={[{}]}>
              {(fields, { add, remove }) => (
                <>
                  <Col xs={24} lg={24}>
                    {fields.map(({ key, name, fieldKey }) => (
                      <Row
                        key={key}
                        style={{ marginBottom: 0 }}
                        className="d-flex justify-content-between"
                      >
                        <Col xs={11}>
                          <Form.Item
                            name={[name, "type"]}
                            fieldKey={[fieldKey, "type"]}
                            rules={[
                              {
                                required: true,
                                message: "Please select the recipient type",
                              },
                            ]}
                            initialValue={"employee"}
                          >
                            <Select
                              placeholder="Type"
                              onChange={(value) => {
                                form.setFieldsValue({
                                  [`${name}.type`]: value,
                                });
                              }}
                            >
                              <Option value="employee">Employee</Option>
                            </Select>
                          </Form.Item>
                        </Col>
                        <Col xs={11}>
                          <Form.Item
                            name={[name, "letter_to"]}
                            fieldKey={[fieldKey, "letter_to"]}
                            rules={[
                              {
                                required: true,
                                message: "Please select the letter to",
                              },
                            ]}
                          >
                            <Select
                              placeholder="Communication Letter To"
                              options={
                                dropdownOptions[
                                  form.getFieldValue([
                                    "recipients",
                                    name,
                                    "type",
                                  ])
                                ]
                              }
                              showSearch
                              filterOption={(input, option) =>
                                option.label
                                  ?.toLowerCase()
                                  .includes(input?.toLowerCase())
                              }
                            />
                          </Form.Item>
                        </Col>
                        <Col xs={2}>
                          <Button
                            onClick={() => {
                              remove(name);
                            }}
                            icon={<DeleteOutlined />}
                            disabled={fields.length === 0}
                          />
                        </Col>
                      </Row>
                    ))}
                  </Col>
                  <Row>
                    <Button
                      type="dashed"
                      onClick={() => add()}
                      icon={<PlusCircleOutlined />}
                    >
                      Add Recipient
                    </Button>
                  </Row>
                </>
              )}
            </Form.List>
          </Row>

          <Form.Item>
            <Row className="d-flex justify-content-end">
              <Row>
                <Button
                  className="me-2"
                  danger
                  onClick={() => {
                    setShowAddModal(false);
                    setShowEditModal(false);
                  }}
                  disabled={isLoading}
                >
                  Cancel
                </Button>
                <AddSubmitButton form={form} isLoading={isLoading} />
              </Row>
            </Row>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Review Communication Letter"
        centered
        open={showViewModal}
        footer={null}
        onCancel={() => {
          setShowViewModal(false);
          resetValues();
        }}
        width={800}
      >
        <Form
          className="mt-3 mx-3"
          form={form}
          name="editForm"
          layout="vertical"
        >
          <Row>
            <Col xs={24} lg={8}>
              <Form.Item
                name="letter_date"
                label="Date Posted"
                rules={[
                  {
                    required: true, whitespace: true,
                  },
                ]}
              >
                <DatePicker disabled />
              </Form.Item>
            </Col>
            <Col xs={24} lg={8}>
              <Form.Item
                name="letter_from"
                label="Letter From"
                rules={[
                  {
                    required: true, whitespace: true,
                  },
                ]}
              >
                <Input disabled />
              </Form.Item>
            </Col>
            <Col xs={24} lg={8}>
              <Form.Item
                name="letter_type_id"
                label="Letter Type"
                rules={[
                  {
                    required: true, whitespace: true,
                  },
                ]}
              >
                <Select options={letterTypes} disabled />
              </Form.Item>
            </Col>
            <Col xs={24} lg={24}>
              <Form.Item
                name="subject"
                label="Subject"
                rules={[
                  {
                    required: true, whitespace: true,
                  },
                ]}
              >
                <Input disabled />
              </Form.Item>
            </Col>
            <Col xs={24} lg={24}>
              <Form.Item
                name="content"
                label="Content"
                rules={[
                  {
                    required: true, whitespace: true,
                  },
                ]}
              >
                <ReactQuillEditor content={content} isDisabled={true} />
              </Form.Item>
            </Col>
          </Row>
          <Row>
            <span className="red me-1">*</span> Recipients
            <Form.List name="recipients" initialValue={[{}]}>
              {(fields, { add, remove }) => (
                <>
                  <Col xs={24} lg={24}>
                    {fields.map(({ key, name, fieldKey }) => (
                      <Row
                        key={key}
                        style={{ marginBottom: 0 }}
                        className="d-flex justify-content-between"
                      >
                        <Col xs={12}>
                          <Form.Item
                            name={[name, "type"]}
                            fieldKey={[fieldKey, "type"]}
                            rules={[
                              {
                                required: true, whitespace: true,
                                message: "Please select the recipient type",
                              },
                            ]}
                          >
                            <Select placeholder="Type" disabled>
                              <Option value="employee">Employee</Option>
                            </Select>
                          </Form.Item>
                        </Col>
                        <Col xs={12}>
                          <Form.Item
                            name={[name, "letter_to"]}
                            fieldKey={[fieldKey, "letter_to"]}
                            rules={[
                              {
                                required: true, whitespace: true,
                                message: "Please select the letter to",
                              },
                            ]}
                          >
                            <Select
                              disabled
                              placeholder="Communication Letter To"
                              options={
                                dropdownOptions[
                                  form.getFieldValue([
                                    "recipients",
                                    name,
                                    "type",
                                  ])
                                ]
                              }
                            />
                          </Form.Item>
                        </Col>
                      </Row>
                    ))}
                  </Col>
                </>
              )}
            </Form.List>
          </Row>

          <Form.Item>
            <Row className="d-flex justify-content-end mt-4">
              <Button
                danger
                onClick={() => setShowViewModal(false)}
                disabled={isLoading}
              >
                Cancel
              </Button>
              {isAdminLogin && clickedRowData.status === "pending" && (
                <>
                  <Button
                    type="text"
                    onClick={() => {
                      setShowViewModal(false);
                      // refreshPage();
                      setShowEditModal(true);
                    }}
                    icon={<EditOutlined />}
                    disabled={clickedRowData.status === "approved"}
                  >
                    Edit
                  </Button>
                  <Button
                    type="text"
                    onClick={() => {
                      confirmApprove(clickedRowData, "approve");
                    }}
                    icon={<CheckCircleOutlined />}
                    disabled={clickedRowData.status === "approved"}
                  >
                    Approve
                  </Button>
                  <Button
                    type="text"
                    onClick={() => {
                      confirmApprove(clickedRowData, "disapprove");
                    }}
                    icon={<CloseCircleOutlined />}
                    disabled={
                      clickedRowData.status === "disapproved" ||
                      clickedRowData.status === "approved"
                    }
                    style={{ color: "red" }}
                  >
                    Disapprove
                  </Button>
                </>
              )}
              {isAdminLogin && clickedRowData.status === "approved" && (
                 <Button
                 type="text"
                 onClick={() => {
                  fetchInitialAttachments();
        
                 }}
                 icon={<CheckCircleOutlined />}
                 disabled={clickedRowData.status !== "approved"}
               >
                 Upload Requirements
               </Button>
              )}

              <Button
                type="text"
                onClick={() => {
                  navigate("view", { state: clickedRowData });
                }}
                icon={<EyeOutlined />}
              >
                View
              </Button>
              <Button
                type="text"
                danger
                onClick={() => {
                  confirmDelete(clickedRowData);
                }}
                icon={<DeleteOutlined />}
              >
                Delete
              </Button>
            </Row>
          </Form.Item>
        </Form>
      </Modal>

      <Modal title="Upload Attachments"
        centered
        open={showUploadModal}
        footer={null}
        onCancel={() => {
          setShowUploadModal(false);
          resetValues();
        }}
        width={800}>
          <div className="py-4 px-3">
          <Upload
              multiple={true}
              onRemove={(file) => {
                const index = fileAttachments.indexOf(file);
                const newFileList = fileAttachments.slice();
                newFileList.splice(index, 1);
                setFileAttachments(newFileList);
              }}
              beforeUpload={(file) => {
                const isSizeValid = file.size / 1024 / 1024 < 2; // Check if file size is less than 1MB (backend request)
                if (!isSizeValid) {
                  toast.error("File size cannot exceed 2MB")
                  return Upload.LIST_IGNORE
                } else {
                  setFileAttachments([...fileAttachments, file]);
                }
                return false
              }}
              fileList={fileAttachments}
              showUploadList={true}
            >
              <Button icon={<UploadOutlined />}>Select Files to Upload</Button>
            </Upload>
            <Row className="d-flex justify-content-end mt-4">
              <Row>
                <Button
                  className="me-2"
                  danger
                  onClick={() => {
                    setShowUploadModal(false);
                    resetValues();
                  }}
                  disabled={isLoading}
                >
                  Cancel
                </Button>
                <Button type="primary" disabled={fileAttachments.length===0} onClick={handleUploadRequirements}>Save</Button>
              </Row>
            </Row>
          </div>

      </Modal>
      {contextHolder}
    </Navbar>
  );
}
